import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Loader as LoaderIcon } from "lucide-react";
import {
  WHATSAPP_IMAGE_MIME_TYPES,
  type WhatsAppSelectedPhoneNumber,
} from "@multi-chat/shared";
import { isApiError } from "../../../api/client.js";
import { updateWhatsAppBusinessProfilePicture } from "../../../api/whatsapp-api.js";
import { ImageDropzone } from "../../../components/common/ImageDropzone.js";
import { ChannelIcon } from "../../../components/channel/ChannelIcon.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog/index.js";
import { Button } from "../../../components/ui/button/index.js";
import { MixedLocaleValue } from "../TechnicalValue.js";
import { cn } from "../../../lib/utils.js";
import {
  classifyWhatsAppProfilePictureSelection,
  readImageFileDimensions,
} from "../profile-picture-validation.js";

const IMAGE_ACCEPT = WHATSAPP_IMAGE_MIME_TYPES.join(",");

/** Full-viewport flex positioner so transform-based dialog animation cannot un-center the panel. */
export const PROFILE_PHOTO_DIALOG_POSITIONER_CLASS =
  "group top-auto left-auto inset-0 flex h-auto w-auto max-h-none translate-x-0 translate-y-0 items-center justify-center overflow-hidden border-0 bg-transparent p-4 shadow-none outline-none animate-none data-[state=closed]:animate-none sm:p-6";

export const PROFILE_PHOTO_DIALOG_PANEL_CLASS =
  "grid w-[min(92vw,28rem)] max-h-[min(85dvh,calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-2rem))] gap-4 overflow-auto rounded-lg border border-border bg-surface-elevated p-6 shadow-md group-data-[state=open]:animate-dialog-in-mauto group-data-[state=closed]:animate-dialog-out-mauto motion-reduce:animate-none motion-reduce:transition-none";

export function WhatsAppPhoneProfileAvatar({
  phone,
  canUpdate,
  onOpen,
  refreshing = false,
}: {
  phone: WhatsAppSelectedPhoneNumber;
  canUpdate: boolean;
  onOpen: () => void;
  refreshing?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const src = phone.profilePictureUrl;
  const label = src
    ? t("whatsapp.connected.profilePhoto.change")
    : t("whatsapp.connected.profilePhoto.add");

  if (!canUpdate) {
    return (
      <span
        className="relative size-12 shrink-0 overflow-hidden rounded-full border border-line bg-surface-muted"
        aria-hidden="true"
      >
        <WhatsAppPhoneAvatar src={src} iconSize={22} refreshing={refreshing} />
      </span>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        "group relative size-12 shrink-0 overflow-hidden rounded-full border border-line bg-surface-muted",
        "cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-brand/35",
      )}
      onClick={onOpen}
      aria-label={label}
      aria-busy={refreshing || undefined}
    >
      <WhatsAppPhoneAvatar src={src} iconSize={22} refreshing={refreshing} />
      {refreshing ? null : (
        <span className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <Camera className="size-4 text-white" aria-hidden="true" />
        </span>
      )}
    </button>
  );
}

function WhatsAppProfileFallback({ iconSize }: { iconSize: number }): React.JSX.Element {
  return (
    <span className="flex size-full items-center justify-center text-[#25D366]">
      <ChannelIcon platformType="WHATSAPP" size={iconSize} />
    </span>
  );
}

export function WhatsAppPhoneAvatar({
  src,
  iconSize = 22,
  refreshing = false,
}: {
  src: string | null | undefined;
  iconSize?: number;
  refreshing?: boolean;
}): React.JSX.Element {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);
  const body = !src || broken ? (
    <WhatsAppProfileFallback iconSize={iconSize} />
  ) : (
    <img
      src={src}
      alt=""
      className="size-full object-cover"
      onError={() => setBroken(true)}
    />
  );
  return (
    <span className="relative block size-full">
      {body}
      {refreshing ? (
        <span
          className="absolute inset-0 flex items-center justify-center bg-black/40"
          data-profile-photo-refreshing=""
        >
          <LoaderIcon
            className="size-4 animate-[spin_0.8s_linear_infinite] text-white motion-reduce:animate-none"
            aria-hidden="true"
          />
        </span>
      ) : null}
    </span>
  );
}

export function WhatsAppPhoneProfilePhotoDialog({
  organizationId,
  phone,
  open,
  onOpenChange,
  onUpdated,
}: {
  organizationId: number;
  phone: WhatsAppSelectedPhoneNumber | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (input: {
    externalPhoneNumberId: string;
    profilePictureUrl: string | null;
  }) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setError(null);
      setDimensions(null);
      setSaving(false);
    }
  }, [open]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!phone) return null;

  async function onSelectFile(next: File) {
    setError(null);
    const pixels = await readImageFileDimensions(next);
    const issue = classifyWhatsAppProfilePictureSelection({
      mimeType: next.type,
      byteSize: next.size,
      width: pixels?.width ?? null,
      height: pixels?.height ?? null,
    });
    if (issue === "invalidType" || issue === "undecodable") {
      setFile(null);
      setDimensions(null);
      setError(t("whatsapp.connected.profilePhoto.invalidType"));
      return;
    }
    if (issue === "tooLarge") {
      setFile(null);
      setDimensions(null);
      setError(t("whatsapp.connected.profilePhoto.tooLarge"));
      return;
    }
    setFile(next);
    setDimensions(pixels);
    if (issue === "tooSmall") {
      setError(
        t("whatsapp.connected.profilePhoto.tooSmall", {
          width: pixels?.width,
          height: pixels?.height,
        }),
      );
    }
  }

  async function onSave() {
    if (!file || !phone) return;
    const pixels = dimensions ?? (await readImageFileDimensions(file));
    const issue = classifyWhatsAppProfilePictureSelection({
      mimeType: file.type,
      byteSize: file.size,
      width: pixels?.width ?? null,
      height: pixels?.height ?? null,
    });
    if (issue) {
      if (issue === "tooSmall") {
        setError(
          t("whatsapp.connected.profilePhoto.tooSmall", {
            width: pixels?.width,
            height: pixels?.height,
          }),
        );
      } else if (issue === "tooLarge") {
        setError(t("whatsapp.connected.profilePhoto.tooLarge"));
      } else {
        setError(t("whatsapp.connected.profilePhoto.invalidType"));
      }
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await updateWhatsAppBusinessProfilePicture(
        organizationId,
        phone.externalPhoneNumberId,
        file,
      );
      onUpdated({
        externalPhoneNumberId: phone.externalPhoneNumberId,
        profilePictureUrl: result.profilePictureUrl,
      });
      onOpenChange(false);
    } catch (caught) {
      if (isApiError(caught) && caught.code === "VALIDATION_FAILED") {
        const message = caught.message.toLowerCase();
        if (message.includes("too small")) {
          setError(
            t("whatsapp.connected.profilePhoto.tooSmall", {
              width: pixels?.width,
              height: pixels?.height,
            }),
          );
        } else if (message.includes("too large")) {
          setError(t("whatsapp.connected.profilePhoto.tooLarge"));
        } else if (message.includes("type") || message.includes("unsupported")) {
          setError(t("whatsapp.connected.profilePhoto.invalidType"));
        } else {
          setError(t("whatsapp.connected.profilePhoto.updateFailed"));
        }
      } else {
        setError(t("whatsapp.connected.profilePhoto.updateFailed"));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className={PROFILE_PHOTO_DIALOG_POSITIONER_CLASS}
        data-profile-photo-dialog=""
      >
        <div className={PROFILE_PHOTO_DIALOG_PANEL_CLASS} data-profile-photo-dialog-panel="">
          <AlertDialogTitle>{t("whatsapp.connected.profilePhoto.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            <MixedLocaleValue>{phone.displayName ?? phone.phoneNumber}</MixedLocaleValue>
          </AlertDialogDescription>
          <ImageDropzone
            previewUrl={previewUrl}
            fileName={file?.name ?? null}
            byteSize={file?.size ?? null}
            detail={
              dimensions
                ? t("whatsapp.connected.profilePhoto.dimensions", {
                    width: dimensions.width,
                    height: dimensions.height,
                  })
                : null
            }
            accept={IMAGE_ACCEPT}
            disabled={saving}
            busy={saving}
            error={error}
            selectLabel={t("whatsapp.connected.profilePhoto.select")}
            changeLabel={t("whatsapp.connected.profilePhoto.change")}
            removeLabel={t("whatsapp.connected.profilePhoto.removeSelection")}
            dropLabel={t("whatsapp.connected.profilePhoto.drop")}
            emptyHint={t("whatsapp.connected.profilePhoto.hint")}
            onSelectFile={(next) => {
              void onSelectFile(next);
            }}
            onRemove={() => {
              setFile(null);
              setError(null);
              setDimensions(null);
            }}
          />
          <AlertDialogActions>
            <Button type="button" variant="ghost" disabled={saving} onClick={() => onOpenChange(false)}>
              {t("whatsapp.connected.profilePhoto.cancel")}
            </Button>
            <Button type="button" disabled={!file || saving || Boolean(error)} onClick={() => void onSave()}>
              {saving
                ? t("whatsapp.connected.profilePhoto.saving")
                : t("whatsapp.connected.profilePhoto.save")}
            </Button>
          </AlertDialogActions>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
